// ============================================================================
// TournamentStartCountdown - "Tournament starting in Ns..." overlay
// ============================================================================
// Renders a centered banner on the game page while gameState.handStartsAt
// is in the future. Self-removes when the countdown elapses. Used at the
// start of a tournament so players have a few seconds to load in, see
// their cards, and orient before the first action begins.
// ============================================================================
import React, { useEffect, useState } from 'react';
import { Box, HStack, Text } from '@chakra-ui/react';
import { Clock } from 'lucide-react';
import { POKER_COLORS } from '../../theme/designSystem';

interface TournamentStartCountdownProps {
  handStartsAt?: number | null; // epoch ms; if past or absent, nothing renders
}

const TournamentStartCountdown: React.FC<TournamentStartCountdownProps> = ({ handStartsAt }) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!handStartsAt) return;
    if (Date.now() >= handStartsAt) return;
    const interval = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(interval);
  }, [handStartsAt]);

  if (!handStartsAt) return null;
  const remainingMs = handStartsAt - now;
  if (remainingMs <= 0) return null;
  const seconds = Math.ceil(remainingMs / 1000);

  return (
    <Box
      position="fixed"
      top="50%"
      left="50%"
      transform="translate(-50%, -50%)"
      zIndex={8800}
      bg="rgba(13, 31, 46, 0.95)"
      border="2px solid"
      borderColor={POKER_COLORS.cyan}
      borderRadius="2xl"
      px={8}
      py={6}
      boxShadow={`0 0 0 4px ${POKER_COLORS.cyan}22, 0 20px 60px rgba(0,0,0,0.6)`}
      backdropFilter="blur(6px)"
      pointerEvents="none"
    >
      <HStack spacing={4}>
        <Clock size={28} color={POKER_COLORS.cyan} />
        <Box>
          <Text fontSize="xs" color="gray.400" letterSpacing="widest" fontWeight="semibold">
            TOURNAMENT STARTING
          </Text>
          <Text fontSize="4xl" fontWeight="bold" color={POKER_COLORS.cyan} lineHeight="1" mt={1}>
            {seconds}s
          </Text>
        </Box>
      </HStack>
    </Box>
  );
};

export default TournamentStartCountdown;
